import { useState, useEffect, useRef } from "react";
import { Button, Textarea, Spinner, MessageBar } from "@fluentui/react-components";
import { Dismiss24Regular, Save24Regular, ArrowReset24Regular } from "@fluentui/react-icons";
import { validateYaml } from "../../services/prompt";
import styles from "./SystemPromptPopup.module.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prompt: string) => Promise<void>;
  onReset: () => Promise<void>;
  savedPrompt: string | null;
  freshPrompt: string;
  isLoading: boolean;
}

export const SystemPromptPopup = ({ isOpen, onClose, onSave, onReset, savedPrompt, freshPrompt, isLoading }: Props) => {
  const editorValue = savedPrompt ?? freshPrompt;

  const [prompt, setPrompt] = useState(editorValue);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [yamlError, setYamlError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const timeoutRefs = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    setPrompt(editorValue);
    setHasChanges(false);
  }, [editorValue, isOpen]);

  useEffect(() => {
    setHasChanges(prompt !== editorValue);

    if (prompt) {
      const validation = validateYaml(prompt);
      setYamlError(validation.isValid ? null : validation.error || "Invalid YAML format");
    } else {
      setYamlError(null);
    }
  }, [prompt, editorValue]);

  useEffect(() => {
    const refs = timeoutRefs.current;
    return () => {
      refs.forEach(t => clearTimeout(t));
      refs.clear();
    };
  }, []);

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await onSave(prompt);
      setSuccessMessage("System prompt saved locally");
      setHasChanges(false);
      const t1 = setTimeout(() => setSuccessMessage(null), 3000);
      timeoutRefs.current.add(t1);
    } catch (error) {
      console.error("Failed to save system prompt:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (isResetting) return;

    setIsResetting(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await onReset();
      setSuccessMessage("Refreshed from server");
      setHasChanges(false);
      const t2 = setTimeout(() => setSuccessMessage(null), 3000);
      timeoutRefs.current.add(t2);
    } catch (error) {
      console.error("Failed to refresh system prompt:", error);
      setErrorMessage("Failed to refresh. Please check your connection and try again.");
      const t3 = setTimeout(() => setErrorMessage(null), 5000);
      timeoutRefs.current.add(t3);
    } finally {
      setIsResetting(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (!confirmClose) return;
    }
    onClose();
  };

  const hasCustom = savedPrompt !== null && savedPrompt !== freshPrompt;

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.popup} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>System Prompt Configuration</h2>
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={handleClose}
            disabled={isSaving || isResetting}
            className={styles.closeButton}
          />
        </div>

        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="medium" />
              <p>Loading system prompt...</p>
            </div>
          ) : (
            <>
              <div className={styles.promptSection}>
                <div className={styles.promptHeader}>
                  <label className={styles.label}>
                    System Prompt Configuration
                    {hasCustom && <span className={styles.customBadge}>Custom</span>}
                  </label>
                </div>
                <Textarea
                  value={prompt || ""}
                  onChange={(_, data) => setPrompt(data.value)}
                  className={`${styles.textarea} ${styles.maximized}`}
                  disabled={isSaving || isResetting}
                  style={{
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: "13px",
                    borderColor: "#d0d7de",
                    borderRadius: "8px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    padding: "20px",
                    backgroundColor: "rgba(0, 0, 0, 0.06)"
                  }}
                />
                {yamlError && (
                  <div className={styles.errorMessage}>
                    <MessageBar
                      intent="error"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#dc3545",
                        padding: "8px 0"
                      }}
                    >
                      YAML Error: {yamlError}
                    </MessageBar>
                  </div>
                )}
                {successMessage && (
                  <div className={styles.successMessage}>
                    <MessageBar
                      intent="success"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#28a745",
                        padding: "8px 0"
                      }}
                    >
                      {successMessage}
                    </MessageBar>
                  </div>
                )}
                {errorMessage && (
                  <div className={styles.errorMessage}>
                    <MessageBar
                      intent="error"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#dc3545",
                        padding: "8px 0"
                      }}
                    >
                      {errorMessage}
                    </MessageBar>
                  </div>
                )}
              </div>

              <div className={styles.footer}>
                <div className={styles.leftButtons}>
                  <Button
                    appearance="secondary"
                    icon={<ArrowReset24Regular />}
                    onClick={handleReset}
                    disabled={isSaving || isResetting}
                    className={styles.actionButton}
                  >
                    {isResetting ? (
                      <>
                        <Spinner size="tiny" />
                        Refreshing...
                      </>
                    ) : (
                      "Refresh from Server"
                    )}
                  </Button>
                </div>

                <div className={styles.rightButtons}>
                  <Button appearance="subtle" onClick={handleClose} disabled={isSaving || isResetting} className={styles.cancelButton}>
                    Cancel
                  </Button>
                  <Button
                    appearance="primary"
                    icon={<Save24Regular />}
                    onClick={handleSave}
                    disabled={!hasChanges || !!yamlError || isSaving || isResetting}
                    className={styles.primaryButton}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="tiny" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
